import { MessageDirection } from '@chatscope/chat-ui-kit-react/src/types/unions';

type SourceDocumentMetadata = {
    filename: string;
    source: string;
    page: number;
    model: string;
    media_type?: string;
    start?: number;
    max_page?: number;
    end?: number;
    score?: number;
    source_pdf_preview_url?: string;
    source_thumbnail_preview_url?: string;
    vid?: string;
};

export type SourceDocument = {
    content?: string;
    metadata: SourceDocumentMetadata;
    messageId?: string;
    id?: string;
    isHidden?: boolean;
    tenantModel?: string;
};

type ExpertDetails = {
    name: string;
    date: string;
    expert_response?: string;
    active?: boolean;
};

export type Disclaimer = {
    disclaimer_type: DisclaimerType;
    disclaimer_text: string;
    appear_in_question: boolean;
    appear_in_answer: boolean;
    keywords: string;
    rowid: string;
    tag_ids: string[];
};

export type MessageInfo = {
    chatID: string;
    rowID: string;
    message: string;
    sentTime: string;
    sender: MessageSenderType;
    direction: MessageDirection;
    sourceDocuments?: Array<SourceDocument>;
    relatedSourceDocuments?: Array<SourceDocument>;
    condensedQuestion?: string;
    feedbackId?: string;
    questionId?: string;
    usefulCount?: number;
    userResponded?: boolean;
    canProvideFeedback?: boolean;
    triageLink?: string;
    triageSolutionCount?: number;
    siLink?: string;
    useful?: boolean;
    promptInfo?: PromptInfo | Record<string, never>;
    noResponse?: boolean;
    model?: string;
    productDetails?: IntegratedModel;
    expertDetails?: ExpertDetails;
    disclaimers?: Array<Disclaimer>;
    id?: string;
};

export type PopoverRow = {
    show: Boolean;
    title: string;
    onClick?: Function;
    icon?: JSX.Element;
    dataTestId?: string;
    withSeparator?: boolean;
    isSelected?: boolean;
    isDisabled?: boolean;
    isProcessing?: boolean;
    tooltip?: string;
    RightComponent?: any;
    isNotSelectable?: boolean;
};

export type SfdcIds = {
    sessionId: string;
    userId: string;
    orgId: string;
};

export const enum SmallWindowState {
    Initial = 'initial',
    Hidden = 'hidden',
    Wide = 'wide',
    Minimized = 'minimized',
    Maximized = 'maximized',
    Remove = 'remove'
}

export const enum AppTypes {
    PortalChat = 'portal chat',
    Portal = 'portal',
    Triage = 'triage',
    DataManagement = 'data management'
}

export const enum Panel {
    FirstRequestPanel = AppTypes.Portal,
    CoPilotPanel = AppTypes.PortalChat,
    DataManagementPanel = AppTypes.DataManagement
}

interface ProductIds {
    productTypeId: string;
    manufacturerId: string;
    productId: string;
}

export interface IntegratedModel {
    modelName: string;
    modelDisplay?: string;
    productTypeName?: string;
    productTypeDisplay?: string;
    manufacturerName?: string;
    manufacturerDisplay?: string;
    productIds?: ProductIds;
    isLinkedProductDetails?: boolean;
}

export interface ProductDetailsResponsePayload {
    producttype: ProductResponse;
    manufacturer: ProductResponse;
    model: ProductResponse;
}

export interface ProductDetailsResponse {
    product: Array<ProductDetailsResponsePayload>;
}

interface ProductResponse {
    name: string;
    id: string;
    display: string | null;
    knowledge_names?: string[];
}

export const enum RecommendedQuestionsSource {
    Observation,
    Solution
}

export type FetchedSuggestions = {
    solutions: string[];
    observations: string[];
};

type Suggestions = {
    suggestions: string[];
};
export type RespType = {
    data?: Suggestions;
};

type PromptInfo = {
    default_standalone_prompt?: string;
    is_complete_prompt?: boolean;
    suggested_alternatives?: string[];
};

export enum FeedbackValue {
    Like = 'Like',
    Dislike = 'Dislike',
    Same = 'Same',
    Better = 'Better',
    Worse = 'Worse'
}

export type FeedbackCategory = {
    original_value: string;
    translated_value: string;
    id: string;
    locale: string;
    order: string;
};

export enum FeedbackType {
    Source = 'Source',
    Response = 'Response',
    Rewrite = 'Rewrite'
}

export type QuestionPreset = {
    original_value: string;
    translated_value: string;
    id: string;
    locale: string;
    order: string;
};

export type FeedbackCategories = {
    question: string;
    chips: string[];
};

type InvestigationDetails = {
    productTypeId: string;
    manufacturerId: string;
    productId: string;
    observation: string;
};

export type MessageInvestigationDetails = {
    numberOfSolutions: number;
    observationObject: Record<string, any>;
    investigationDetails: InvestigationDetails;
};

export type MagicButtonRect = {
    top: number;
    left: number;
    msgLeft: number;
    msgTop: number;
};

export const enum MessageSenderType {
    User = 'User',
    Aquant = 'Aquant',
    UserMock = 'UserMock'
}

export type ChatHistorySessionPayload = {
    tenant_model: string;
    search_term: string;
    is_descending: boolean;
    offset: number;
    limit: number;
};

export type ChatHistorySessionPayloadByModels = {
    search_term: string;
    offset: number;
    limit: number;
    limit_per_model: number;
};

export type SingleChatHistoryData = {
    investigation_id: string;
    user_email: string;
    chat_id: string;
    tenant_model: string;
    model_serial_number?: string;
    question: string;
    created_at: string;
    user_external_id: string;
    triage_product_id: string;
    summary: string | null;
};

export type ChatHistoryData = {
    tenant_model?: string | null;
    has_more?: boolean;
    data?: SingleChatHistoryData[];
};

export const enum SortingType {
    Date = 'date',
    Model = 'model'
}

type HighlightInProgressData = {
    question: string;
    partial_answer: string;
};

export type HighlightSuggestionPayload = {
    highlight: string;
    chat_history_line_id: string;
    stream_in_progress_data: HighlightInProgressData;
};

export enum DisclaimerType {
    Caution = 'Caution',
    Notice = 'Notice'
}

export enum DisclaimerTriggerBy {
    keywords = 'Keywords',
    Tags = 'Tags'
}

export type CaseInfo = {
    investigation_id: string;
    asset_id: string;
    work_order_id: string;
};

export enum GenerationType {
    SemanticOnly = 'SemanticOnly',
    Interleaved = 'Interleaved',
    LexicalOnly = 'LexicalOnly'
}

export enum ResponseDepth {
    Concise = 'Concise',
    Detailed = 'Detailed'
}

export type AskStreamCustomInstructions = {
    response_depth: ResponseDepth;
};

export type AskStreamHandlers = {
    onAskInit?: (abortController: AbortController) => void;
    onError?: (ex: any) => void;
    onStreamInit?: (reader: ReadableStreamDefaultReader<Uint8Array>) => void;
    handleAnswer?: (answer: string) => void;
};

export enum SOURCE_DOCUMENT_DOMAIN {
    AWS = 's3.amazonaws.com'
}

export enum STORAGE {
    AWS = 'AWS',
    AZURE = 'AZURE'
}
